/**
 * Gemini Image Generation Service (나노바나나 / Nano Banana)
 * Google Gemini API를 통한 이미지 생성
 */

const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models';

// 모델 옵션
const MODELS = {
  NANO_BANANA_2: 'gemini-2.0-flash-exp-image-generation', // Nano Banana 2 (이미지 생성 전용)
  NANO_BANANA_PRO: 'gemini-2.0-flash-exp-image-generation', // Nano Banana Pro
  NANO_BANANA: 'gemini-2.0-flash-exp-image-generation', // 기본 Nano Banana
};

/**
 * Gemini API 설정 확인
 */
export const isConfigured = () => {
  const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
  return apiKey && apiKey !== 'your_gemini_api_key_here' && apiKey.length > 10;
};

/**
 * 이미지 생성 (텍스트 → 이미지)
 * @param {string} prompt - 이미지 생성 프롬프트
 * @param {object} options - 옵션 (aspectRatio, style 등)
 * @returns {Promise<{url: string, base64: string, mimeType: string}>}
 */
export const generateImage = async (prompt, options = {}) => {
  const apiKey = import.meta.env.VITE_GEMINI_API_KEY;

  if (!apiKey || apiKey === 'your_gemini_api_key_here') {
    throw new Error('Gemini API 키가 설정되지 않았습니다. .env 파일에 VITE_GEMINI_API_KEY를 설정해주세요.');
  }

  const {
    aspectRatio = '16:9',
    style = 'photorealistic',
    model = MODELS.NANO_BANANA_2,
  } = options;

  // 스타일에 따른 프롬프트 수정
  const stylePrompts = {
    photorealistic: 'Create a photorealistic image: ',
    illustration: 'Create an illustration style image: ',
    abstract: 'Create an abstract artistic image: ',
    minimal: 'Create a minimal, clean design image: ',
    infographic: 'Create a professional infographic visual: ',
  };

  const enhancedPrompt = `${stylePrompts[style] || ''}${prompt}. High quality, professional, suitable for news media.`;

  try {
    const response = await fetch(
      `${GEMINI_API_URL}/${model}:generateContent?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: enhancedPrompt,
                },
              ],
            },
          ],
          generationConfig: {
            responseModalities: ['TEXT', 'IMAGE'],
            responseMimeType: 'text/plain',
          },
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Gemini API Error ${response.status}: ${errorData.error?.message || response.statusText}`
      );
    }

    const data = await response.json();

    // 응답에서 이미지 데이터 추출
    const candidates = data.candidates || [];
    if (candidates.length === 0) {
      throw new Error('이미지 생성 응답이 없습니다.');
    }

    const parts = candidates[0].content?.parts || [];
    let imageData = null;
    let textDescription = '';

    for (const part of parts) {
      if (part.inlineData) {
        imageData = {
          base64: part.inlineData.data,
          mimeType: part.inlineData.mimeType || 'image/png',
        };
      }
      if (part.text) {
        textDescription = part.text;
      }
    }

    if (!imageData) {
      // 이미지가 없으면 텍스트 응답만 반환
      return {
        url: null,
        base64: null,
        mimeType: null,
        description: textDescription,
        error: '이미지를 생성할 수 없습니다. 프롬프트를 수정해 주세요.',
      };
    }

    // Base64를 Data URL로 변환
    const dataUrl = `data:${imageData.mimeType};base64,${imageData.base64}`;

    return {
      url: dataUrl,
      base64: imageData.base64,
      mimeType: imageData.mimeType,
      description: textDescription,
    };
  } catch (error) {
    console.error('Gemini Image Generation Error:', error);
    throw error;
  }
};

/**
 * 인포그래픽용 이미지 생성
 * @param {string} topic - 인포그래픽 주제
 * @param {object} data - 인포그래픽 데이터
 * @returns {Promise<{url: string, base64: string}>}
 */
export const generateInfographic = async (topic, data = {}) => {
  const { sections = [], style = 'modern' } = data;

  // 데이터를 프롬프트에 포함
  let dataDescription = '';
  if (sections.length > 0) {
    dataDescription = sections
      .map((s) => `- ${s.title}: ${s.value}`)
      .join('\n');
  }

  const prompt = `Professional infographic about "${topic}".
${dataDescription ? `Key data points:\n${dataDescription}` : ''}
Style: ${style}, clean modern design, data visualization, icons, clear typography.
Color scheme: Professional business colors.
Layout: Clear visual hierarchy, easy to read.`;

  return generateImage(prompt, {
    aspectRatio: '9:16', // 인포그래픽은 세로 비율
    style: 'infographic',
  });
};

/**
 * 뉴스 기사용 이미지 생성
 * @param {string} headline - 기사 제목
 * @param {string} content - 기사 내용 요약
 * @returns {Promise<{url: string, base64: string}>}
 */
export const generateNewsImage = async (headline, content = '') => {
  const prompt = `News article illustration for: "${headline}".
${content ? `Context: ${content.substring(0, 200)}` : ''}
Professional, editorial style, suitable for Seoul Economic Daily newspaper.
Clean, modern, journalistic aesthetic.`;

  return generateImage(prompt, {
    aspectRatio: '16:9',
    style: 'photorealistic',
  });
};

export default {
  isConfigured,
  generateImage,
  generateInfographic,
  generateNewsImage,
  MODELS,
};
